import { create } from 'zustand';
import { toast } from 'sonner';
import {
  fetchBriefing,
  setBriefingItemStatus,
  type Briefing,
  type BriefingItem,
} from '@/api/briefing';
import { fetchEmailsByIds, type EmailMailboxMembership } from '@/api/emails';
import type { Email } from '@/types/email';
import { useAccountStore } from '@/stores/accountStore';
import { useEmailStore } from '@/stores/emailStore';
import { useFolderStore } from '@/stores/folderStore';
import { useUndoStore, type CustomUndoHandlers } from '@/stores/undoStore';
import { executeEmailAction } from '@/stores/emailCommands';
import { showUndoToast } from '@/lib/undoToast';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';

/**
 * Collapsed kind sections on the briefing page. Purely presentational, so it
 * lives in localStorage (per device) rather than server settings — this
 * replaced the old per-kind rows/collapsed display setting.
 */
const COLLAPSED_KINDS_STORAGE_KEY = 'briefing:collapsedKinds';

function loadCollapsedKinds(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KINDS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const result: Record<string, boolean> = {};
      for (const [kind, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (value === true) {
          result[kind] = true;
        }
      }
      return result;
    }
  } catch {
    // Corrupt/unavailable storage: start expanded.
  }
  return {};
}

export type BriefingScope =
  | { type: 'kind'; kind: string }
  | { type: 'item'; accountId: string; key: string }
  | null;

export interface AccountBriefing {
  briefing: Briefing;
  items: BriefingItem[];
}

export interface BriefingRefEntry {
  email: Email;
  /** Message currently has an inbox membership (IMAP folder or Gmail label). */
  inInbox: boolean;
}

const refKey = (accountId: string, messageId: string) => `${accountId}:${messageId}`;

export function briefingItemIdentity(item: BriefingItem): string {
  return `${item.accountId}:${item.key}`;
}

interface BriefingStoreState {
  entries: AccountBriefing[] | null;
  isLoading: boolean;
  error: string | null;
  /** Item keys with an in-flight status change (renders dimmed/disabled). */
  pendingKeys: Set<string>;
  /** Kind sections the user collapsed on the briefing page (device-local). */
  collapsedKinds: Record<string, boolean>;
  /** `${accountId}:${messageId}` -> hydrated ref email + membership info. */
  refEmails: Record<string, BriefingRefEntry>;
  scope: BriefingScope;
  fetchRequestId: number;

  fetchForAccount: (accountId: string) => Promise<void>;
  handleBriefingUpdatedEvent: (accountId: string) => void;
  markDone: (item: BriefingItem) => Promise<void>;
  /**
   * Compound action: archive the item's referenced emails that are still in
   * the inbox AND resolve the item as 'actioned' (vs. Done's 'dismissed' —
   * the agent sees the difference as handled-vs-irrelevant). One undo entry
   * reverses both halves.
   */
  archiveAndDone: (item: BriefingItem, refs: Array<{ email: Email; inInbox: boolean }>) => Promise<void>;
  toggleKindCollapsed: (kind: string) => void;
  enterKindScope: (kind: string) => void;
  enterItemScope: (
    item: BriefingItem,
    rowRefs?: Array<{ email: Email; inInbox: boolean }>
  ) => Promise<void>;
  clearScope: () => void;
  exitScope: () => Promise<void>;
  reset: () => void;
}

/** All refs of the given items, grouped per owning account. */
function collectRefsByAccount(entries: AccountBriefing[]): Map<string, string[]> {
  const byAccount = new Map<string, Set<string>>();
  for (const entry of entries) {
    for (const item of entry.items) {
      for (const ref of item.messageRefs) {
        if (!ref.message_id) continue;
        const accountId = ref.account_id ?? item.accountId;
        const bucket = byAccount.get(accountId) ?? new Set<string>();
        bucket.add(String(ref.message_id));
        byAccount.set(accountId, bucket);
      }
    }
  }
  const result = new Map<string, string[]>();
  byAccount.forEach((ids, accountId) => result.set(accountId, Array.from(ids)));
  return result;
}

function membershipInInbox(mailboxes: EmailMailboxMembership[]): boolean {
  return mailboxes.some((membership) => membership.role === 'inbox');
}

async function hydrateRefEmails(entries: AccountBriefing[]): Promise<Record<string, BriefingRefEntry>> {
  const refsByAccount = collectRefsByAccount(entries);
  const result: Record<string, BriefingRefEntry> = {};
  await Promise.all(
    Array.from(refsByAccount.entries()).map(async ([accountId, ids]) => {
      // The endpoint caps at 100 ids; briefing items cap at 100 x 50 refs in
      // theory, but a run carries far fewer. Chunk defensively.
      for (let start = 0; start < ids.length; start += 100) {
        const chunk = ids.slice(start, start + 100);
        try {
          const fetched = await fetchEmailsByIds(accountId, chunk);
          for (const { email, mailboxes } of fetched) {
            result[refKey(accountId, email.id)] = {
              email: email as Email,
              inInbox: membershipInInbox(mailboxes),
            };
          }
        } catch (error) {
          console.error('[briefingStore] failed to hydrate refs', { accountId, error });
        }
      }
    })
  );
  return result;
}

export const useBriefingStore = create<BriefingStoreState>()((set, get) => {
  const removeItemLocal = (item: BriefingItem) => {
    set((state) => {
      if (!state.entries) return {};
      return {
        entries: state.entries.map((entry) => ({
          ...entry,
          items: entry.items.filter(
            (candidate) => !(candidate.accountId === item.accountId && candidate.key === item.key)
          ),
        })),
      };
    });
  };

  const restoreItemLocal = (item: BriefingItem) => {
    set((state) => {
      if (!state.entries) return {};
      return {
        entries: state.entries.map((entry) => {
          if (entry.briefing.accountId !== item.accountId) return entry;
          if (entry.items.some((candidate) => candidate.key === item.key)) return entry;
          return { ...entry, items: [...entry.items, item] };
        }),
      };
    });
  };

  // Don't strand a drill-down: resolving the last item of a kind scope (or the
  // item an item-scope is showing) exits the scope.
  const settleScopeAfterResolve = (item: BriefingItem) => {
    const { scope, entries } = get();
    if (scope?.type === 'kind') {
      const anyLeft = entries?.some((entry) =>
        entry.items.some((candidate) => candidate.kind === scope.kind)
      );
      if (!anyLeft) {
        set({ scope: null });
      }
    } else if (
      scope?.type === 'item' &&
      scope.key === item.key &&
      scope.accountId === item.accountId
    ) {
      void get().exitScope();
    }
  };

  const loadAccounts = async (targetIds: string[], requestId: number) => {
    const results = await Promise.all(
      targetIds.map((id) =>
        fetchBriefing(id).catch((error: unknown) => {
          console.error('[briefingStore] fetch failed', { accountId: id, error });
          return null;
        })
      )
    );
    if (get().fetchRequestId !== requestId) return;

    const entries: AccountBriefing[] = [];
    for (const result of results) {
      if (result?.briefing) {
        entries.push({ briefing: result.briefing, items: result.items });
      }
    }

    const refEmails = await hydrateRefEmails(entries);
    if (get().fetchRequestId !== requestId) return;

    set({ entries, refEmails, isLoading: false, error: null });
  };

  return {
    entries: null,
    isLoading: false,
    error: null,
    pendingKeys: new Set<string>(),
    collapsedKinds: loadCollapsedKinds(),
    refEmails: {},
    scope: null,
    fetchRequestId: 0,

    fetchForAccount: async (accountId) => {
      if (!accountId) return;
      const requestId = get().fetchRequestId + 1;
      set({ fetchRequestId: requestId, isLoading: true, error: null });
      try {
        const realAccounts = useAccountStore
          .getState()
          .accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID);
        const targetIds =
          accountId === UNIFIED_ACCOUNT_ID ? realAccounts.map((account) => account.id) : [accountId];
        if (!targetIds.length) {
          set({ entries: [], refEmails: {}, isLoading: false });
          return;
        }
        await loadAccounts(targetIds, requestId);
      } catch (error) {
        if (get().fetchRequestId !== requestId) return;
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load briefing',
        });
      }
    },

    handleBriefingUpdatedEvent: (accountId) => {
      // Only refresh when briefing data is in use (briefing mode fetched it).
      const state = get();
      if (state.entries === null) return;
      const selectedAccountId = useAccountStore.getState().selectedAccountId;
      if (!selectedAccountId) return;
      if (selectedAccountId !== UNIFIED_ACCOUNT_ID && selectedAccountId !== accountId) return;
      void state.fetchForAccount(selectedAccountId);
    },

    markDone: async (item) => {
      set((state) => ({ pendingKeys: new Set(state.pendingKeys).add(item.key) }));
      try {
        await setBriefingItemStatus(item.accountId, item.key, 'dismissed');
        removeItemLocal(item);
        settleScopeAfterResolve(item);
        const label = item.title.length > 60 ? `${item.title.slice(0, 57)}…` : item.title;
        const entryId = useUndoStore.getState().recordAction({
          type: 'custom',
          items: [],
          description: `Done: ${label}`,
          custom: {
            undo: async () => {
              try {
                await setBriefingItemStatus(item.accountId, item.key, 'active');
                restoreItemLocal(item);
                return true;
              } catch (error) {
                console.error('Failed to restore briefing item:', error);
                return false;
              }
            },
            redo: async () => {
              try {
                await setBriefingItemStatus(item.accountId, item.key, 'dismissed');
                removeItemLocal(item);
                return true;
              } catch (error) {
                console.error('Failed to re-dismiss briefing item:', error);
                return false;
              }
            },
          },
        });
        showUndoToast(`Done: ${label}`, entryId);
      } catch (error) {
        console.error('Failed to update briefing item:', error);
        toast.error('Failed to update briefing item');
      } finally {
        set((state) => {
          const next = new Set(state.pendingKeys);
          next.delete(item.key);
          return { pendingKeys: next };
        });
      }
    },

    archiveAndDone: async (item, refs) => {
      set((state) => ({ pendingKeys: new Set(state.pendingKeys).add(item.key) }));
      try {
        const label = item.title.length > 60 ? `${item.title.slice(0, 57)}…` : item.title;
        const companion: CustomUndoHandlers = {
          undo: async () => {
            try {
              await setBriefingItemStatus(item.accountId, item.key, 'active');
              restoreItemLocal(item);
              return true;
            } catch (error) {
              console.error('Failed to restore briefing item:', error);
              return false;
            }
          },
          redo: async () => {
            try {
              await setBriefingItemStatus(item.accountId, item.key, 'actioned');
              removeItemLocal(item);
              return true;
            } catch (error) {
              console.error('Failed to re-action briefing item:', error);
              return false;
            }
          },
        };

        const inInboxRefs = refs.filter((ref) => ref.inInbox && ref.email?.id);
        if (!inInboxRefs.length) {
          // Every ref already left the inbox — nothing to archive, so this
          // degrades to resolving the item (as 'actioned', not 'dismissed').
          await setBriefingItemStatus(item.accountId, item.key, 'actioned');
          removeItemLocal(item);
          settleScopeAfterResolve(item);
          const entryId = useUndoStore.getState().recordAction({
            type: 'custom',
            items: [],
            description: `Done: ${label}`,
            custom: companion,
          });
          showUndoToast(`Done: ${label}`, entryId);
          return;
        }

        // Group by (account, folder) — in practice one group: the owning
        // account's inbox. The companion rides the FIRST group's undo entry
        // (multi-group items are a unified-view rarity; their extra groups
        // undo as plain email moves).
        const groups = new Map<string, { accountId: string; folderId: string; ids: string[] }>();
        for (const ref of inInboxRefs) {
          const accountId = ref.email.accountId ?? item.accountId;
          const folderId = ref.email.folderId ?? '';
          const groupKey = `${accountId}:${folderId}`;
          const bucket = groups.get(groupKey) ?? { accountId, folderId, ids: [] };
          bucket.ids.push(ref.email.id);
          groups.set(groupKey, bucket);
        }

        let anySucceeded = false;
        let attachCompanion = true;
        for (const group of groups.values()) {
          const result = await executeEmailAction('archive', { kind: 'messages', ids: group.ids }, {
            contextAccountId: group.accountId,
            contextFolderId: group.folderId || null,
            undoLabel: `Archived & done: ${label}`,
            ...(attachCompanion ? { companionUndo: companion } : {}),
          });
          if (result.failedIds.length < group.ids.length) {
            anySucceeded = true;
            attachCompanion = false;
          }
        }

        if (!anySucceeded) {
          // The engine already restored the emails and reported the failure;
          // leave the item active so nothing is half-resolved.
          return;
        }

        await setBriefingItemStatus(item.accountId, item.key, 'actioned');
        removeItemLocal(item);
        settleScopeAfterResolve(item);
      } catch (error) {
        console.error('Failed to archive & resolve briefing item:', error);
        toast.error('Failed to archive & resolve briefing item');
      } finally {
        set((state) => {
          const next = new Set(state.pendingKeys);
          next.delete(item.key);
          return { pendingKeys: next };
        });
      }
    },

    toggleKindCollapsed: (kind) => {
      set((state) => {
        const next = { ...state.collapsedKinds };
        if (next[kind]) {
          delete next[kind];
        } else {
          next[kind] = true;
        }
        try {
          localStorage.setItem(COLLAPSED_KINDS_STORAGE_KEY, JSON.stringify(next));
        } catch {
          // Storage full/unavailable: in-memory state still works this session.
        }
        return { collapsedKinds: next };
      });
    },

    enterKindScope: (kind) => {
      // Pure briefing-store view state: the email list itself is untouched, so
      // exiting a kind scope needs no mailbox reload.
      set({ scope: { type: 'kind', kind } });
      useEmailStore.getState().clearSelection();
    },

    enterItemScope: async (item, rowRefs) => {
      const state = get();
      // Prefer the refs the ROW resolved (live list copy or hydrated) so the
      // drill-down shows exactly what the card's "N messages" promised — the
      // internal refEmails-only derivation is the fallback, not the source of
      // truth (it can lag the live list).
      const refs: Array<{ email: Email; inInbox: boolean }> =
        rowRefs && rowRefs.length
          ? rowRefs
          : item.messageRefs
              .map((ref) => state.refEmails[refKey(ref.account_id ?? item.accountId, String(ref.message_id))])
              .filter((entry): entry is BriefingRefEntry => Boolean(entry));
      if (!refs.length) return;

      set({ scope: { type: 'item', accountId: item.accountId, key: item.key } });
      useEmailStore.getState().clearSelection();
      // Same single-writer path as the sender-group drill-down: the referenced
      // emails become the visible list, actions/undo/advance all work on them.
      useEmailStore.getState().invalidateLoadRequests();
      useEmailStore.getState().replaceList(refs.map((entry) => entry.email), {
        source: { kind: 'briefingItem', itemKey: briefingItemIdentity(item) },
        hasMore: false,
        loading: false,
        initialLoading: false,
      });
    },

    /**
     * Navigation reset: drop any drill-down scope WITHOUT the exitScope
     * reload — callers are navigation paths that reload the mailbox
     * themselves (sidebar click, folder/account switch, external reset).
     * Releases list ownership too: the reload-guard defers mailbox reloads
     * while listSource is 'briefingItem', so leaving it set would defer the
     * very reload the caller is about to run. (A reload queued during the
     * scope may drain later as a duplicate — harmless; eagerly clearing it
     * here could race the reload handler's own queueing and eat a real one.)
     */
    clearScope: () => {
      if (!get().scope) {
        return;
      }
      set({ scope: null });
      if (useEmailStore.getState().listSource.kind === 'briefingItem') {
        useEmailStore.setState({ listSource: { kind: 'mailbox' } });
      }
    },

    exitScope: async () => {
      const { scope } = get();
      if (!scope) return;
      set({ scope: null });
      if (scope.type === 'item') {
        // The item drill-down replaced the email list — restore the mailbox.
        // Any reload deferred WHILE the scope owned the list is satisfied by
        // this fresh load; drop it so the drain side doesn't double-load.
        const { useSenderGroupingStore } = await import('./senderGroupingStore');
        useSenderGroupingStore.setState({ pendingMailboxReload: null });
        const accountId = useAccountStore.getState().selectedAccountId;
        const folderId = useFolderStore.getState().selectedFolderId();
        useEmailStore.getState().clearSelection();
        if (accountId && folderId) {
          await useEmailStore.getState().loadEmails(accountId, folderId, { reset: true });
        }
      }
    },

    reset: () => {
      set((state) => ({
        entries: null,
        isLoading: false,
        error: null,
        pendingKeys: new Set<string>(),
        refEmails: {},
        scope: null,
        fetchRequestId: state.fetchRequestId + 1,
      }));
    },
  };
});
